/* eslint-disable no-useless-catch */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Paper, { PaperProps } from '@mui/material/Paper';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  main_text_matched_substrings: readonly MainTextMatchedSubstrings[];
  secondary_text?: string;
}
interface PlaceType {
  description: string;
  structured_formatting: StructuredFormatting;
}

const useEnhancedEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

function CustomPaper(props: PaperProps) {
  return (
    <Paper {...props}>
      {props.children}
      <Box
        sx={(staticTheme) => ({
          display: 'flex',
          justifyContent: 'flex-end',
          p: 1,
          pt: '1px',
          ...staticTheme.applyStyles('dark', {
            opacity: 0.8,
          }),
        })}
      >
        <img
          src={
            'https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png'
          }
          alt=""
          width="120"
          height="14" />
      </Box>
    </Paper>
  );
}

const fetch = throttle(
  async (
    request: { input: string; sessionToken: any },
    callback: (results?: readonly PlaceType[]) => void,
  ) => {
    try {
      const { suggestions } = await (
        window as any
      ).google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        request,
      );

      callback(
        suggestions.map((suggestion: any) => {
          const place = suggestion.placePrediction;
          // Map to the old AutocompleteService.getPlacePredictions format
          // https://developers.google.com/maps/documentation/javascript/places-migration-autocomplete
          return {
            description: place.text.text,
            structured_formatting: {
              main_text: place.mainText.text,
              main_text_matched_substrings: place.mainText.matches.map(
                (match: any) => ({
                  offset: match.startOffset,
                  length: match.endOffset - match.startOffset,
                }),
              ),
              secondary_text: place.secondaryText?.text,
            },
          };
        }),
      );
    } catch (err: any) {
      throw err;
    }
  },
  400,
);

const emptyOptions = [] as any;
let sessionToken: any;

interface MapsAutoCompleteProps {
  address: string;
  onAddressSelect: Function;
  setNewAddressInput: Function;
}

export default function MapsAutoComplete({ onAddressSelect, address, setNewAddressInput }: MapsAutoCompleteProps) {
  const [value, setValue] = React.useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<readonly PlaceType[]>(emptyOptions);

  useEnhancedEffect(() => {
    if (inputValue === '') {
      setOptions(value ? [value] : emptyOptions);
      return undefined;
    }

    let active = true;

    if (!sessionToken) {
      sessionToken = new (
        window as any
      ).google.maps.places.AutocompleteSessionToken();
    }

    fetch({ input: inputValue, sessionToken }, (results?: readonly PlaceType[]) => {
      if (!active) {
        return;
      }

      let newOptions: readonly PlaceType[] = [];

      if (results) {
        newOptions = results;

        if (value) {
          newOptions = [
            value,
            ...results.filter((result) => result.description !== value.description),
          ];
        }
      } else if (value) {
        newOptions = [value];
      }
      setOptions(newOptions);
    });

    return () => {
      active = false;
    };
  }, [value, inputValue]);

  const handlePlaceSelect = async (place: PlaceType | null ) => {
    if (place) {
      try {
        const geocoder = new (window as any).google.maps.Geocoder();
        const results = await new Promise<any[]>((resolve, reject) => {
          geocoder.geocode({ address: place.description }, (res: any, status: string) => {
            if (status === 'OK') resolve(res);
            else reject(status);
          });
        });

        if (results.length > 0 && onAddressSelect) {
          const addressComponents = results[0].address_components;
          onAddressSelect(addressComponents);
        }
      } catch (err) {
        console.error('Geocoding failed', err);
      }
    }
  };

  React.useEffect(() => {
    if (address !== inputValue) {
      setInputValue(address);
      setValue( {
        description: address,
        structured_formatting: {
          main_text: address,
          main_text_matched_substrings: [],
          secondary_text: '',
        },
      },
      );
    }
  }, [address]);


  return (
    <Autocomplete
      fullWidth
      sx={{ mb: 8 }}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={(x) => x}
      slotProps={{
        paper: CustomPaper as PaperProps,
      }}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={inputValue}
      freeSolo
      noOptionsText="No locations"
      onChange={(
        event: React.SyntheticEvent<Element, Event>,
        value: string | PlaceType | null,
        reason: any,
        details?: any,
      ) => {
        if (typeof value === 'string') {
          setValue({
            description: value,
            structured_formatting: {
              main_text: value,
              main_text_matched_substrings: [],
              secondary_text: '',
            },
          });

          setNewAddressInput(value);

        } else if (value) {
          setOptions(value ? [value, ...options] : options);
          handlePlaceSelect(value);
        }
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
        setNewAddressInput(newInputValue);
      }}
      renderInput={(params) => (
        <TextField {...params}
          required
          label="Address Line 1"
          fullWidth />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const matches = option.structured_formatting.main_text_matched_substrings;

        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match: any) => [match.offset, match.offset + match.length]),
        );
        return (
          <li key={key}
            {...optionProps}>
            <Grid container
              sx={{ alignItems: 'center' }}>
              <Grid sx={{ display: 'flex', width: 44 }}>
                <LocationOnIcon sx={{ color: 'text.secondary' }} />
              </Grid>
              <Grid sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                {parts.map((part: { highlight: any; text: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; }, index: React.Key | null | undefined) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{
                      fontWeight: part.highlight
                        ? 'fontWeightBold'
                        : 'fontWeightRegular',
                    }}
                  >
                    {part.text}
                  </Box>
                ))}
                {option.structured_formatting.secondary_text ? (
                  <Typography 
                    sx={{ color: 'text.secondary' }}>
                    {option.structured_formatting.secondary_text}
                  </Typography>
                ) : null}
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}